import React, { useState, useEffect } from 'react';
import { Container, TextField, Button, Grid, Paper, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'; // or any other Prism style you prefer

import './App.css';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#2F80FF',
    },
    text: {
      primary: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: 'Montserrat, Arial, sans-serif',
    h5: {
      fontWeight: 700,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: '16px',
          fontWeight: 700,
          borderRadius: '20px',  // Set border radius to 20px for buttons
          position: 'relative',
          '&:hover': {
            backgroundColor: '#4175BD',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: '20px', // Set border radius for TextField
          '& .MuiOutlinedInput-root': {
            borderRadius: '20px', // Also apply to the inner input
            '& fieldset': {
              border: 'none', // Remove the border
            },
          },
          backgroundColor: '#535353',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: '20px', // Set border radius for ToggleButton
          border: 'none',        // Remove the border for ToggleButton
          '&.Mui-selected': {
            backgroundColor: '#2F80FF', // Custom background when selected
            color: '#FFF',
            '&:hover': {
              backgroundColor: '#4175BD',
            },
          },
          padding: '10px 20px', // Add padding to the button
          margin: '0 5px',      // Add margin between buttons
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        grouped: {
          borderRadius: '20px',  // Ensure the group also has rounded corners
          '&:not(:last-child)': {
            borderRight: 'none', // Remove the border between buttons
          },
        },
      },
    },
  },
});


const customStyle = {
  ...vscDarkPlus,
  'pre[class*="language-"]': {
    ...vscDarkPlus['pre[class*="language-"]'],
    background: '#535353',
  },
  'code[class*="language-"]': {
    ...vscDarkPlus['code[class*="language-"]'],
    background: '#535353',
  },
};
const jsonFieldMapping = {
  navn: 'Name',
  kontaktoplysning3: 'Website',
  kontaktoplysning1: 'Phone',
  kontaktoplysning2: 'Email',
  pNummer: 'P_Number',
  kortBeskrivelse: 'Short_company_form',
  langBeskrivelse: 'Long_company_form',
  vejnavn: 'Street',
  husnummerFra: 'House_number',
  etage: 'Floor',
  postnummer: 'Zip_Code',
  postdistrikt: 'City',
  //bynavn: 'Bynavn',
  branchekode: 'Industry_code',
  branchetekst: 'Industry_text',
  //nyesteErstMaanedsbeskaeftigelse: 'Latest Monthly Employment',
  sammensatStatus: 'Status',
  stiftelsesDato: 'Establishment_Date',
};
const transformDataForJSON = (data) => {
  return Object.keys(data).reduce((transformed, key) => {
    if (jsonFieldMapping[key]) {
      transformed[jsonFieldMapping[key]] = data[key];
    } else {
      transformed[key] = data[key]; // Preserve other fields if needed
    }
    return transformed;
  }, {});
};

const JSONDisplay = ({ data }) => (
  <Paper elevation={0} style={styles.paper}>
    <SyntaxHighlighter language="json" style={customStyle}>
      {JSON.stringify(data, null, 2)}
    </SyntaxHighlighter>
  </Paper>
);

const styles = {
  paper: {
    padding: '20px',
    marginTop: '20px',
    width: '100%',
    overflowX: 'auto',
    backgroundColor: '#535353',
    //border: '1px solid #FFFFFF',
    borderRadius: '40px',
    marginLeft: '5px',
  },
  json: {
    fontFamily: 'monospace',
    fontSize: '16px',
    color: 'white',
  },
  buttonIcon: {
    position: 'absolute',
    right: '20px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '20px',
    height: 'auto',
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  boxView: {
    padding: '10px',
    margin: '10px 0',
    //border: '1px solid #FFFFFF',
    borderRadius: '20px',
    backgroundColor: '#535353',
    color: '#BABABA',
    textAlign: 'left',
  },
  twoColumnGrid: {
    padding: '10px',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)', // Two columns
    gap: '15px', // Space between columns
    width: '100%',
  },
};

const fieldMapping = {
  navn: 'Name',
  kontaktoplysning3: 'Website',
  kontaktoplysning1: 'Phone',
  kontaktoplysning2: 'Email',
  pNummer: 'P Number',
  kortBeskrivelse: 'Short company form',
  langBeskrivelse: 'Long company form',
  vejnavn: 'Street',
  husnummerFra: 'House number',
  etage: 'Floor',
  postnummer: 'Zip Code',
  postdistrikt: 'City',
  //bynavn: 'Bynavn',
  branchekode: 'Industry code',
  branchetekst: 'Industry text',
  //nyesteErstMaanedsbeskaeftigelse: 'Latest Monthly Employment',
  sammensatStatus: 'Status',
  stiftelsesDato: 'Establishment Date',
};


const FriendlyDisplay = ({ data }) => {
  const mappedFields = Object.keys(fieldMapping)
    .filter((key) => data[key] !== undefined) // Only show fields that exist in the data
    .map((key) => ({
      label: fieldMapping[key],
      value: data[key],
    }));

  return (
    <Grid container spacing={2} style={{margin: '5px', marginLeft: '10px'}}>
      {mappedFields.map((field, index) => (
        <Grid item xs={12} sm={6} key={index}> {/* Adjusted to have two columns */}
          <TextField
            fullWidth
            label={field.label}
            value={typeof field.value === 'object' ? JSON.stringify(field.value, null, 2) : field.value}
            variant="outlined"
            InputProps={{
              readOnly: true,
              style: { 
                pointerEvents: 'none',  // Prevents mouse interactions
                cursor: 'default',      // Changes cursor to default when hovering
              },
            }}
            style={{ backgroundColor: '#535353' }} // Keep consistent background color
          />
        </Grid>
      ))}
    </Grid>
  );
};

const App = () => {
  const [responseData, setResponseData] = useState(
    Object.keys(fieldMapping).reduce((acc, key) => {
      acc[key] = ''; // Initialize each field with an empty string
      return acc;
    }, {})
  );
  const [viewMode, setViewMode] = useState('friendly'); // Default view mode
  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const cvr = formData.get('cvr');

    try {
      // Construct the query parameters for the API URL
      const queryParams = new URLSearchParams({
        query: cvr, // Use the CVR number as the query
      }).toString();

      const url = `${process.env.REACT_APP_API_URL}/fetchdata?${queryParams}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          "Authorization": `Bearer ${process.env.REACT_APP_CVR_API_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      delete data.direktoer;
      delete data.bestyrelsesmedlemmer;
      delete data.finance;
      delete data.telefaxNummer;
      setResponseData(data); 
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="sm">
        <Grid container spacing={2} alignItems="center" justifyContent="center" style={{ marginTop: '20px' }}>
          <Grid item xs={12}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2} alignItems="center" justifyContent="center">
                <Grid item xs={8}>
                  <TextField fullWidth label="CVR Number" name="cvr" variant="outlined" />
                </Grid>
                <Grid item xs={8}>
                  <div style={styles.buttonWrapper}>
                    <Button type="submit" variant="contained" color="primary" fullWidth>
                      Get data
                      <img src={`${process.env.PUBLIC_URL}/arrow.svg`} style={styles.buttonIcon} alt="Arrow icon" />
                    </Button>
                  </div>
                </Grid>
              </Grid>
            </form>
          </Grid>

          {/* Toggle Button for JSON or Friendly Display */}
          {responseData && (
          <Grid item xs={12}>
            <Grid container justifyContent="center"> {/* This ensures centering */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                aria-label="View mode"
              >
                <ToggleButton value="friendly" aria-label="Show Friendly View">
                  Table
                </ToggleButton>
                <ToggleButton value="json" aria-label="Show JSON">
                  JSON
                </ToggleButton>
                
              </ToggleButtonGroup>
            </Grid>
          </Grid>
        )}

          {/* Conditionally render JSON or Friendly view based on viewMode */}
          {responseData && viewMode === 'json' && <JSONDisplay data={transformDataForJSON(responseData)} />}
          {responseData && viewMode === 'friendly' && <FriendlyDisplay data={responseData} />}
        </Grid>
      </Container>
    </ThemeProvider>
  );
};

export default App;
